import { Icon } from "@iconify/react";
import { filter } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import plusFill from "@iconify/icons-eva/plus-fill";
import { formatRut, RutFormat } from "@fdograph/rut-utilities";
import {
  Button,
  Card,
  Container,
  Grid,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Titulo } from "../components/Titulo";
import { removeAccents } from "src/utils/converter";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import Scrollbar from "../components/Scrollbar";
import SearchNotFound from "../components/SearchNotFound";
import { TablaHead, TablaToolbar } from "../components/_dashboard/tablas";
import * as locales from "@material-ui/core/locale";
import Page from "../components/Page";
import AgregarSucursal from "../components/AdministracionSucursal/AgregarSucursal";
import { Alerta } from "../components/Alerta";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import { useSelector } from "react-redux";
import { sucursalesApi } from "src/services/sucursalesApi";
import { AlertaModalV2 } from "src/components/AlertaSwall";
import { useSnackbar } from "src/components/snackbar";
import { empleadosApi } from "src/services/empleadosApi";

const TABLE_HEAD = [
  { id: "ID", label: "ID Sucursal" },
  { id: "Nombre", label: "Nombre sucursal", alignRight: false },
  { id: "direccion", label: "Dirección", alignRight: false },
  { id: "telefono", label: "Teléfono", alignRight: false },
  { id: "gerente", label: "Nombre Gerente", alignRight: false },
  { id: "razonSocial", label: "Razón Social", alignRight: false },
  { id: "rutRazonSocial", label: "Rut Razón Social", alignRight: false },
  // Contribuyente con el que la oficina emite ante OpenFactura: lo determina la
  // credencial configurada, así que puede no ser la sociedad de la oficina. Va
  // junto al RUT de esa sociedad para poder contrastarlos de un vistazo. Se
  // pinta con rutEmisorOF y apiOpenFacturaConfigurada, que llegan porque
  // /sucursales devuelve la fila completa; si esa respuesta pasara a declarar
  // sus campos uno por uno, hay que nombrar los dos ahí o la columna dirá que
  // ninguna oficina tiene credencial.
  { id: "rutEmisorOF", label: "Cuenta emisora", alignRight: false },
  { id: "representanteLegal", label: "Representante", alignRight: false },
  { id: "rutRepresentante", label: "Rut Representante", alignRight: false },
  { id: "" },
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(array, (_user) => {
      return (
        removeAccents(_user.nombre.toLowerCase()).indexOf(
          removeAccents(query.toLowerCase()),
        ) !== -1 ||
        _user.direccion.toLowerCase().indexOf(query.toLowerCase()) !== -1 ||
        _user.telefono.toLowerCase().indexOf(query.toLowerCase()) !== -1 ||
        removeAccents(_user.gerente.toLowerCase()).indexOf(
          removeAccents(query.toLowerCase()),
        ) !== -1 ||
        removeAccents(_user.razonSocial.toLowerCase()).indexOf(
          removeAccents(query.toLowerCase()),
        ) !== -1 ||
        _user.rutRazonSocial.toLowerCase().indexOf(query.toLowerCase()) !==
          -1 ||
        // La cuenta emisora viene nula mientras la oficina no tenga credencial
        // de OpenFactura: sin el valor por omisión, escribir en el buscador
        // rompe la tabla entera.
        (_user.rutEmisorOF || "").toLowerCase().indexOf(query.toLowerCase()) !==
          -1 ||
        removeAccents(_user.representanteLegal.toLowerCase()).indexOf(
          removeAccents(query.toLowerCase()),
        ) !== -1 ||
        _user.rutRepresentante.toLowerCase().indexOf(query.toLowerCase()) !== -1
      );
    });
  }
  return stabilizedThis.map((el) => el[0]);
}

// La cuenta emisora tiene tres estados, no dos. Una oficina puede tener credencial
// de OpenFactura y no tener cuenta registrada: el guardado desde esta pantalla
// escribe las dos juntas, pero la carga inicial de las cuentas deja la oficina sin
// cuenta cada vez que no consigue el contribuyente de su credencial, y esa oficina
// igual emite. Sin separar los dos casos vacíos, la pantalla diría que no hay nada
// configurado sobre una oficina que está emitiendo, que es lo contrario de lo que
// el administrador viene a verificar acá.
function CuentaEmisora({ rutEmisorOF, apiOpenFacturaConfigurada }) {
  if (rutEmisorOF) {
    return formatRut(rutEmisorOF, RutFormat.DOTS_DASH);
  }

  if (apiOpenFacturaConfigurada) {
    return (
      <Tooltip title="La oficina emite con su credencial de OpenFactura, pero su cuenta emisora no quedó registrada. Guarda la credencial otra vez para registrarla.">
        <Typography variant="body2" color="warning.main" noWrap>
          No registrada
        </Typography>
      </Tooltip>
    );
  }

  return (
    <Typography variant="body2" color="text.secondary" noWrap>
      Sin credencial
    </Typography>
  );
}

export default function Sucursales() {
  const { empleado: user, rol: rolObtenido } = useSelector(
    (state) => state.auth.user,
  );
  const [open, setOpen] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [openEditar, setOpenEditar] = useState(false);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState("nombre");
  const [filterName, setFilterName] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [elementoSeleccionado, setElementoSeleccionado] = useState(null);
  const [locale] = useState("esES");
  const { enqueueSnackbar } = useSnackbar();

  const { data: sucursales = [], isLoading: loadingTable } =
    sucursalesApi.useGetSucursalesQuery();

  const [eliminarSucursalDB] = sucursalesApi.useDeleteSucursalMutation();

  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const [addHistorialAcciones] = empleadosApi.useAddHistorialAccionesMutation();

  useEffect(() => {
    let urlActual = window.location.href;
    if (urlActual.includes("http://localhost:3000")) {
    } else {
      addHistorialAcciones({
        user: user.rut,
        body: {
          urlAccion: urlActual,
        },
      })
        .unwrap()
        .then(() => {})
        .catch(() => {});
    }
  }, [addHistorialAcciones, user.rut]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = sucursales.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - sucursales.length) : 0;

  const filteredUsers = applySortFilter(
    sucursales,
    getComparator(order, orderBy),
    filterName,
  );

  const isUserNotFound = filteredUsers.length === 0;

  const eliminarSucursal = (nombre) => {
    AlertaModalV2({
      title: `¿Eliminar la sucursal de ${nombre}?`,
      text: `Se eliminará la sucursal, ¿Está seguro?`,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      icon: "warning",
    })
      .fire()
      .then((result) => {
        if (result.isConfirmed) {
          eliminarSucursalDB(nombre)
            .unwrap()
            .then(() => {
              enqueueSnackbar("Sucursal eliminada correctamente.", {
                variant: "success",
              });
            })
            .catch(() => {
              enqueueSnackbar(
                "Ocurrió un error en el servidor. Inténtelo nuevamente más tarde.",
                {
                  variant: "error",
                },
              );
            });
        }
      });
  };

  return (
    <Page title="TAXTIC">
      <Container maxWidth="xl">
        <Titulo>
          <Grid container>
            <Grid item xs></Grid>
            <Grid item xs={6}>
              <Typography variant="h4" gutterBottom>
                Administración de Sucursales
              </Typography>
            </Grid>
            <Grid item xs>
              {(rolObtenido === "ADMIN" || rolObtenido === "SUPER_ADMIN") && (
                <Button
                  onClick={() => {
                    setOpen(true);
                  }}
                  variant="contained"
                  startIcon={<Icon icon={plusFill} />}
                >
                  Agregar Sucursal
                </Button>
              )}
            </Grid>
          </Grid>
        </Titulo>

        <Card>
          <TablaToolbar
            numSelected={selected.length}
            filterName={filterName}
            onFilterName={handleFilterByName}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              {!loadingTable ? (
                <Table>
                  <TablaHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={sucursales.length}
                    numSelected={selected.length}
                    onRequestSort={handleRequestSort}
                    onSelectAllClick={handleSelectAllClick}
                  />
                  <TableBody>
                    {filteredUsers
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      .map((row) => {
                        const {
                          nombre,
                          label,
                          direccion,
                          telefono,
                          gerente,
                          razonSocial,
                          rutRazonSocial,
                          rutEmisorOF,
                          apiOpenFacturaConfigurada,
                          representanteLegal,
                          rutRepresentante,
                        } = row;
                        const isItemSelected = selected.indexOf(nombre) !== -1;

                        return (
                          <TableRow
                            hover
                            key={nombre}
                            tabIndex={-1}
                            role="checkbox"
                            selected={isItemSelected}
                            aria-checked={isItemSelected}
                          >
                            <TableCell
                              align="center"
                              component="th"
                              scope="row"
                              padding="none"
                            >
                              {nombre}
                            </TableCell>
                            <TableCell
                              align="center"
                              component="th"
                              scope="row"
                              padding="none"
                            >
                              <Typography variant="subtitle2" noWrap>
                                {label}
                              </Typography>
                            </TableCell>
                            <TableCell align="left">{direccion}</TableCell>
                            <TableCell align="left">{telefono}</TableCell>
                            <TableCell align="left">{gerente}</TableCell>
                            <TableCell align="left">{razonSocial}</TableCell>
                            <TableCell align="left">
                              {formatRut(rutRazonSocial, RutFormat.DOTS_DASH)}
                            </TableCell>
                            <TableCell align="left">
                              <CuentaEmisora
                                rutEmisorOF={rutEmisorOF}
                                apiOpenFacturaConfigurada={
                                  apiOpenFacturaConfigurada
                                }
                              />
                            </TableCell>
                            <TableCell align="left">
                              {representanteLegal}
                            </TableCell>
                            <TableCell align="left">
                              {formatRut(rutRepresentante, RutFormat.DOTS_DASH)}
                            </TableCell>
                            <TableCell align="right">
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                mb={5}
                              >
                                <Tooltip title="Editar">
                                  <Button
                                    color="primary"
                                    onClick={() => {
                                      setElementoSeleccionado(row);
                                      setOpenEditar(true);
                                    }}
                                  >
                                    <EditIcon />
                                  </Button>
                                </Tooltip>
                                {rolObtenido === "SUPER_ADMIN" && (
                                  <Tooltip title="Eliminar">
                                    <Button
                                      color="error"
                                      onClick={() => {
                                        eliminarSucursal(row.nombre);
                                      }}
                                    >
                                      <DeleteIcon />
                                    </Button>
                                  </Tooltip>
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={6} />
                      </TableRow>
                    )}
                  </TableBody>
                  {isUserNotFound && (
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                          <SearchNotFound searchQuery={filterName} />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              ) : (
                <Skeleton variant="rectangular" width="100%" height="500px" />
              )}
            </TableContainer>
          </Scrollbar>

          <ThemeProvider
            theme={(outerTheme) => createTheme(outerTheme, locales[locale])}
          >
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={sucursales.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </ThemeProvider>
        </Card>
      </Container>
      <Alerta
        showAlert={showAlert}
        setShowAlert={setShowAlert}
        color="success"
        message="Datos de la sucursal guardados"
      />

      {open && (
        <AgregarSucursal
          open={open}
          setOpen={setOpen}
          nombre=""
          label=""
          direccion=""
          comuna={null}
          telefono=""
          gerente=""
          razonSocial=""
          rutRazonSocial=""
          representanteLegal=""
          rutRepresentante=""
          editar={false}
        />
      )}
      {openEditar && (
        <AgregarSucursal
          open={openEditar}
          setOpen={setOpenEditar}
          nombre={elementoSeleccionado.nombre}
          label={elementoSeleccionado.label}
          direccion={elementoSeleccionado.direccion}
          comuna={elementoSeleccionado.comuna}
          telefono={elementoSeleccionado.telefono}
          gerente={elementoSeleccionado.gerente}
          razonSocial={elementoSeleccionado.razonSocial}
          rutRazonSocial={formatRut(
            elementoSeleccionado.rutRazonSocial.toLowerCase(),
            RutFormat.DASH,
          )}
          representanteLegal={elementoSeleccionado.representanteLegal}
          rutRepresentante={formatRut(
            elementoSeleccionado.rutRepresentante.toLowerCase(),
            RutFormat.DASH,
          )}
          apiOpenFacturaConfigurada={
            elementoSeleccionado.apiOpenFacturaConfigurada
          }
          apiWebpayConfigurada={elementoSeleccionado.apiWebpayConfigurada}
          webpayCommerceCode={elementoSeleccionado.webpayCommerceCode}
          rutEmisorOF={elementoSeleccionado.rutEmisorOF}
          editar={true}
        />
      )}
    </Page>
  );
}
